import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, ListChecks } from "lucide-react";
import {
  Button,
  Callout,
  ColumnsMenu,
  EmptyState,
  FilterChip,
  FilterSidebar,
  FiltersButton,
  LinkButton,
  TextInput,
  errorText,
  useColumnVisibility,
  useNarrow,
} from "@amber/ui";
import type { QuickAction, TaskRow, TasksResponse } from "../../contracts/index";
import { tasksQuery } from "../queries";
import type { TasksSearch } from "../search";
import { filterChips, summaryText } from "./filters";
import { QuickActionDialog } from "./QuickActionDialog";
import { TaskDrawer } from "./TaskDrawer";
import { Sidebar } from "./TasksSidebar";
import { TasksTable } from "./TasksTable";

function TasksEmpty({
  data,
  view,
  scope,
  onShowAll,
}: {
  data: TasksResponse;
  view: "open" | "done";
  scope: TasksSearch["scope"];
  onShowAll: () => void;
}) {
  if (view === "done") return <EmptyState icon={<CheckCircle2 />} title="Nothing was done in the last 30 days" />;
  if (scope !== "all" && data.counts.open > 0)
    return (
      <EmptyState
        icon={<ListChecks />}
        title="Nothing for you to act on"
        actions={<Button onClick={onShowAll}>Show all tasks</Button>}
      >
        {data.counts.open === 1
          ? "1 task is open for other roles."
          : `${String(data.counts.open)} tasks are open for other roles.`}
      </EmptyState>
    );
  return (
    <EmptyState icon={<ListChecks />} title="Nothing is waiting for a person.">
      Everything else runs automatically.
    </EmptyState>
  );
}

function TasksHeading({
  data,
  filtersOpen,
  onToggleFilters,
}: {
  data: TasksResponse | undefined;
  filtersOpen: boolean;
  onToggleFilters: () => void;
}) {
  return (
    <div className="flex flex-wrap items-end gap-3 px-4 pt-3.5 pb-1 max-compact:px-2">
      <div className="min-w-0 flex-1">
        <h1 id="view-title" className="m-0 text-2xl font-semibold">
          Tasks
        </h1>
        <p className="m-0 mt-[3px] text-ui text-muted">
          {data ? (
            <>
              <b className="font-semibold text-fg">{data.counts.actionable}</b> open for your role ·{" "}
              <b className="font-semibold text-fg">{data.counts.high}</b> high priority ·{" "}
            </>
          ) : null}
          Tasks open and close automatically; every manual action is recorded in the audit log
        </p>
      </div>
      <FiltersButton open={filtersOpen} onToggle={onToggleFilters} />
    </div>
  );
}

const TOGGLES = [
  { id: "task", label: "Task", canHide: false },
  { id: "handled", label: "Handled in" },
];

function useDebouncedText(search: TasksSearch, onSearch: (next: TasksSearch) => void) {
  const [text, setText] = useState(search.q ?? "");
  useEffect(() => {
    const timer = window.setTimeout(() => {
      const q = text.trim() || undefined;
      if (q !== search.q) onSearch({ ...search, q });
    }, 300);
    return () => {
      window.clearTimeout(timer);
    };
  }, [text, search, onSearch]);
  return [text, setText] as const;
}

export function TasksView({
  search,
  taskId,
  onSearch,
  onOpenTask,
}: {
  search: TasksSearch;
  taskId: string | null;
  onSearch: (next: TasksSearch) => void;
  onOpenTask: (id: string | null) => void;
}) {
  const query = useQuery(tasksQuery(search));
  const [text, setText] = useDebouncedText(search, onSearch);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const visibility = useColumnVisibility(TOGGLES);
  const [acting, setActing] = useState<{ task: TaskRow; action: QuickAction } | null>(null);
  const narrow = useNarrow();
  const data = query.data;
  const view = search.view ?? "open";
  const set = (patch: Partial<TasksSearch>) => {
    onSearch({ ...search, ...patch });
  };
  const chips = filterChips(search, data);
  const filtered = chips.length > 0;
  const reset = () => {
    setText("");
    onSearch({ view: search.view, scope: search.scope });
  };
  const rows = data?.rows ?? [];
  const act = (task: TaskRow, action: QuickAction) => {
    setActing({ task, action });
  };

  return (
    <div className="relative flex min-h-0 flex-1">
      <FilterSidebar label="Task filters" open={filtersOpen}>
        <Sidebar data={data} search={search} set={set} />
      </FilterSidebar>
      <section aria-labelledby="view-title" className="flex min-h-0 min-w-0 flex-1 flex-col">
        <TasksHeading
          data={data}
          filtersOpen={filtersOpen}
          onToggleFilters={() => {
            setFiltersOpen(!filtersOpen);
          }}
        />
        <div className="flex min-h-10 flex-wrap items-center gap-2 px-4 pb-2 max-compact:px-2">
          <span className="text-md text-muted" role="status">
            {summaryText(data, view, filtered, search.scope)}
          </span>
          {chips.map((c) => (
            <FilterChip
              key={`${c.label}-${c.value}`}
              label={c.label}
              value={c.value}
              onRemove={() => {
                if (c.label === "Text") setText("");
                set(c.clear);
              }}
            />
          ))}
          {filtered ? <LinkButton onClick={reset}>Reset all filters</LinkButton> : null}
          <span className="flex-1" />
          <label className="sr-only" htmlFor="tasks-filter">
            Filter tasks
          </label>
          <TextInput
            id="tasks-filter"
            type="search"
            placeholder="Filter tasks"
            style={{ width: narrow ? 150 : 200 }}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
            }}
          />
          <ColumnsMenu columns={visibility.toggles} />
        </div>
        {query.isError ? (
          <Callout tone="attention" role="alert" className="mx-4">
            Couldn&apos;t load tasks. {errorText(query.error)}{" "}
            <LinkButton
              onClick={() => {
                void query.refetch();
              }}
            >
              Retry
            </LinkButton>
          </Callout>
        ) : null}
        {data && rows.length === 0 && !filtered ? (
          <TasksEmpty
            data={data}
            view={view}
            scope={search.scope}
            onShowAll={() => {
              set({ scope: "all" });
            }}
          />
        ) : (
          <TasksTable
            view={view}
            rows={rows}
            pending={query.isPending}
            noMatches={Boolean(data) && rows.length === 0 && filtered}
            hidden={visibility.hidden}
            taskId={taskId}
            onReset={reset}
            onOpenTask={onOpenTask}
            onAct={act}
          />
        )}
      </section>
      {taskId ? (
        <TaskDrawer
          id={taskId}
          onClose={() => {
            onOpenTask(null);
          }}
          onAction={act}
        />
      ) : null}
      {acting ? (
        <QuickActionDialog
          task={acting.task}
          action={acting.action}
          onClose={() => {
            setActing(null);
          }}
        />
      ) : null}
    </div>
  );
}
